import { Button } from "@mui/material";
import { Box } from "@mui/system";
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useClientAlertsService from "../../services/administration/useClientAlertsService";
import useToast, { ToastPosition, ToastType } from "../../hooks/useToast";
import LicensePlate from "../../components/LicensePlate";
import { ValidateAlertReasons } from "../../types/Alert";

const Container = styled(Box)`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100vh;
    justify-content: center;
`

const Title = styled('h2')`
    text-align: center;
`;

export default function ConfirmAlert() {
    const [ready, isReady] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [alertCannotBeConfirmed, setAlertCannotBeConfirmed] = useState(false);
    const [alertAlreadyConfirmed, setAlertAlreadyConfirmed] = useState(false);
    const [alertConfirmed, setAlertConfirmed] = useState(false);
    const [licensePlateNumber, setLicensePlateNumber] = useState<string | undefined>();

    const { t } = useTranslation("common");
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const id = searchParams.get('id') ?? undefined;
    const { validateAlertExists, confirmAlert } = useClientAlertsService();
    const { showToast } = useToast();


    useEffect(() => {
        if (!id) {
            setAlertCannotBeConfirmed(true);
            return;
        }
        validateAlertExists(id).then(({ data }) => {
            if (!data.status) {
                if (data.reason === ValidateAlertReasons.ALERT_ALREADY_EXECUTED_TODAY) {
                    setAlertAlreadyConfirmed(true);
                } else {
                    setAlertCannotBeConfirmed(true);
                }
            } else {
                setLicensePlateNumber(data.licensePlate);
            }
        }).catch(() => {
            setAlertCannotBeConfirmed(true);
        }).finally(() => {
            isReady(true);
        });
    }, [id, location, validateAlertExists]);

    const handleConfirmAlert = useCallback(() => {
        if (!id) {
            return;
        }

        confirmAlert(id).then(() => {
            setButtonDisabled(true);
            showToast({
                message: t("confirmAlert.alertConfirmed"),
                type: ToastType.SUCCESS,
                position: ToastPosition.TOP_CENTER,
            });

            setTimeout(() => {
                setAlertConfirmed(true);
            }, 5000);
        }).catch(() => {
            showToast({
                message: t("confirmAlert.confirmationError"),
                type: ToastType.ERROR,
                position: ToastPosition.TOP_CENTER,
            });
        });
    }, [id, confirmAlert, showToast, t]);

    if (!ready) {
        return null;
    }

    if (alertAlreadyConfirmed) {
        return (
            <Container>
                <Title>{t("confirmAlert.alertAlreadyConfirmed")}</Title>
            </Container>
        );
    }

    if (alertCannotBeConfirmed) {
        return (
            <Container>
                <Title>{t("confirmAlert.alertCannotBeConfirmed")}</Title>
            </Container>
        );
    }

    if (alertConfirmed) {
        return (
            <Container>
                <Title>{t("confirmAlert.alertConfirmed")}</Title>
            </Container>
        );
    }

    return (
        <Container>
            <LicensePlate licenseNumber={licensePlateNumber ?? ''} />
            <Button
                onClick={handleConfirmAlert}
                variant="outlined"
                color="primary"
                disabled={alertConfirmed || buttonDisabled}
            >
                {t("confirmAlert.confirmAlertButton")}
            </Button>
        </Container>
    );
}
